import {
  IPdfDefinitions,
  IPdfData,
  PdfContentType,
  PdfContentSetting,
  CHARGEABLE_TIPS_DEFINE,
  CHARGEABLE_COLUMN_DEFINE,
} from '@app/shared/common/make-pdf/v2';
import { checkBoxMeasureBuilder } from '@app/shared/common/make-pdf/v2/cell-builders/check-box-measure.builder';
import { getPluralEntityName } from '@app/shared/configs/entity-name-config';
import {
  ENTITY_TYPES,
  TRANSFER_PDF_HEADER_VALUES,
} from '@app/shared/types/enums';
import { getSingleExportFileName } from '@app/shared/utils';
import { get, map } from 'lodash';
import { ContentColumns, ContentText } from 'pdfmake/interfaces';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

const buildPrintSheetPdfConfig = (
  data: IPdfDefinitions['data'],
  bulkOptions?: IPdfDefinitions['bulkOptions'],
  i18nService?: XVII18nService,
): IPdfDefinitions => ({
  data,
  bulkOptions,
  generateFileName: (pdfData): string => {
    const headerPrefix = getPluralEntityName(ENTITY_TYPES.TRANSFER);
    const siteName: string = get(pdfData, 'site_name') as string;
    const printDate = get(pdfData, 'print_date') as string;

    return getSingleExportFileName(
      `${headerPrefix} Print Sheets - From `,
      siteName,
      printDate,
    );
  },
  generateHeader: (pdfData: IPdfData): (ContentText | ContentColumns)[] => {
    return [
      {
        text: i18nService.instant(I18n.Forms.TransferForm.TransferItemBrackets),
        fontSize: 14,
        bold: true,
        margin: [20, 16, 20, 0],
      },
      {
        fontSize: 8,
        margin: [20, 5, 20, 0],
        columns: [
          {
            text: [
              { text: i18nService.instant(I18n.Sites.FromSiteColonAndSpace) },
              { text: get(pdfData, 'site_name', ''), bold: true },
            ],
          },
          {
            text: [
              { text: i18nService.instant(I18n.Sites.ToSiteColonAndSpace) },
              { text: get(pdfData, 'site_to_name', ''), bold: true },
            ],
          },
        ],
      },
    ];
  },
  content: {
    tips: CHARGEABLE_TIPS_DEFINE,
    mainGrids: {
      type: PdfContentType.Grids,
      setting: (): PdfContentSetting => {
        return {
          inventory_items_list: {
            columns: [
              CHARGEABLE_COLUMN_DEFINE,
              {
                path: TRANSFER_PDF_HEADER_VALUES.Name,
                title: I18n.Forms.GridForm.ItemName,
                width: '35%',
                valueGetter: (params): string =>
                  params.value ?? params.rowData?.item_name,
              },
              {
                path: TRANSFER_PDF_HEADER_VALUES.Par,
                title: I18n.Forms.TransferForm.Par,
                width: '15%',
              },
              {
                path: TRANSFER_PDF_HEADER_VALUES.Measures,
                title: I18n.Forms.TransferForm.TransferUnit,
                width: '*',
                builder: checkBoxMeasureBuilder,
              },
              {
                path: TRANSFER_PDF_HEADER_VALUES.QuantityRequest,
                title: I18n.Forms.TransferForm.QtyRequested,
                width: 'auto',
                alignment: 'right',
              },
              {
                path: TRANSFER_PDF_HEADER_VALUES.QuantityReceived,
                title: I18n.Forms.TransferForm.QtyReceived,
                width: 'auto',
                alignment: 'right',
              },
              {
                path: TRANSFER_PDF_HEADER_VALUES.NotReceived,
                title: I18n.Forms.TransferForm.NotReceived,
                width: 'auto',
                alignment: 'right',
              },
            ],
            multiRow: {
              baseOnRowFieldPath: 'measures',
              splitter: (rowData, gridData, grid): IPdfData[] => {
                return map(
                  get(rowData, grid.multiRow.baseOnRowFieldPath, []) as any,
                  (splitData) => {
                    return {
                      measure_description: splitData.description,
                      measure_factor_value: splitData.factor_value,
                      measure_entity_id: splitData.entity_id,
                      measures: rowData?.measures,
                    };
                  },
                );
              },
            },
            title: {
              text: '',
              style: {
                fontSize: 8,
                bold: true,
                margin: [0, 0, 0, 5],
              },
            },
            rowStyle: { bold: false },
            columnDefaultStyle: {
              alignment: 'left',
              width: '*',
            },
            orderBy: ['is_chargeable desc', 'name asc'],
          },
        };
      },
    },
    signatures: {
      type: PdfContentType.Signatures,
      setting: [
        i18nService.translate(I18n.Forms.GridForm.RequestedBy),
        i18nService.translate(I18n.Forms.GridForm.TransferredBy),
        i18nService.translate(I18n.Forms.GridForm.ReceivedBy),
      ],
    },
  },
  options: {
    pageMargins: [20, 50, 20, 60],
  },
});

export { buildPrintSheetPdfConfig };
